import { EndUserPages } from '@aurora/shared-types/pages/enums';
import Head from 'next/head';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import type { PageAndEmptyParams } from '../../../routes/endUserRoutes';
import useEndUserRoutes from '../../../routes/useEndUserRoutes';
import EmptyState from '../../common/EmptyState/EmptyState';
import AppContext from '../../context/AppContext/AppContext';
import ThemeApplicator from '../../themes/ThemeApplicator/ThemeApplicator';
import useTranslation from '../../useTranslation';
import type { Props } from './PageError';
import localStyles from './PageError.module.pcss';

/**
 * Actual PageError component.  Do NOT import this directly -- use PageError instead to enforce a dynamic import.
 * @param statusCode
 * @param title
 * @param description
 * @param icon
 * @param showCommunityLink
 * @constructor
 */
const PageErrorActual: React.FC<React.PropsWithChildren<Props>> = ({
  statusCode = 404,
  title,
  description,
  icon,
  showCommunityLink = true
}): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(SharedComponent.PAGE_ERROR);
  const { Link } = useEndUserRoutes();
  const { community } = useContext(AppContext);

  if (textLoading) {
    return null;
  }

  const headTitleKey = community?.title ? 'headTitle' : 'headTitleNoCommunity';
  const headTitle = formatMessage(headTitleKey, { statusCode, communityTitle: community?.title });
  const pageTitle = title ?? formatMessage('pageTitle', { statusCode });

  return (
    <>
      <Head>
        <title>{headTitle}</title>
      </Head>
      <ThemeApplicator />
      <div className={cx('lia-container')} data-testid="PageError">
        <EmptyState
          title={pageTitle}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(icon && { icon: icon })}
          description={
            description || (showCommunityLink && formatMessage('description', { statusCode }))
          }
        >
          {showCommunityLink && (
            <Link<PageAndEmptyParams> route={EndUserPages.CommunityPage}>
              <a className={cx('d-block small')}>{formatMessage('redirect')}</a>
            </Link>
          )}
        </EmptyState>
      </div>
    </>
  );
};

export default PageErrorActual;
